import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Download, FileText, FolderKanban, KeyRound, Search, Trash2, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { API, del, get, post, qs } from '../api';
import { usePermissions } from '../auth';
import { Confirm, Drawer, Empty, ErrorBox, Loading, PageHeader, Pagination, Progress, StatusBadge, useToast } from '../components/ui';
import { LiveIndicator } from '../components/Layout';
import { STATUS_LABEL, fmtBytes, fmtDate, fmtDuration, fmtNumber, fmtShortDate } from '../format';
import { useGroups } from './shared';
import type { Job, JobStatus, Page, PageReport } from '../types';

const LIMIT = 50;
const STATUSES: JobStatus[] = ['queued', 'processing', 'done', 'failed', 'cancelled'];

export function JobProgress({ job }: { job: Job }) {
  if (job.status === 'processing' && job.pages) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Progress value={job.pages.total ? job.pages.done / job.pages.total : 0} />
        <span className="cell-sub">
          {job.pages.done}/{job.pages.total}
        </span>
      </div>
    );
  }
  if (job.pages) {
    return (
      <span>
        {fmtNumber(job.pages.total)} <span className="cell-sub">({job.pages.ocr} OCR)</span>
      </span>
    );
  }
  return <span className="faint">—</span>;
}

function Creator({ job }: { job: Job }) {
  if (!job.createdBy) return <span className="faint">—</span>;
  const name = job.createdBy.name ?? `#${job.createdBy.id}`;
  if (job.createdBy.type === 'user') return <>{name}</>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }} title={`API key: ${name}`}>
      <KeyRound size={13} className="faint" /> {name}
    </span>
  );
}

export function JobsPage() {
  const perms = usePermissions();
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') as JobStatus | null) ?? undefined;
  const groupId = params.get('groupId') ?? undefined;
  const offset = Number(params.get('offset') ?? 0);
  const selected = params.get('job');
  const [search, setSearch] = useState(params.get('q') ?? '');
  const q = params.get('q') ?? undefined;

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
    if (!('offset' in patch) && !('job' in patch)) next.delete('offset');
    setParams(next, { replace: true });
  };

  // Búsqueda con pequeña espera mientras se escribe.
  useEffect(() => {
    const t = setTimeout(() => (search || undefined) !== q && update({ q: search || undefined }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const groups = useGroups(perms.isSuper);
  const jobs = useQuery({
    queryKey: ['jobs', { status, groupId, q, offset }],
    queryFn: () => get<Page<Job>>(`/jobs${qs({ status, groupId, q, offset, limit: LIMIT })}`),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Trabajos"
        subtitle="Documentos enviados y su estado, actualizado en tiempo real"
        actions={
          <>
            <LiveIndicator />
            {perms.canUpload && (
              <Link to="/upload" className="btn btn-primary">
                <Upload size={16} /> Enviar documentos
              </Link>
            )}
          </>
        }
      />

      <div className="card">
        <div className="toolbar">
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-3)' }} />
            <input
              className="input input-search"
              style={{ paddingLeft: 32 }}
              placeholder="Buscar por nombre de archivo"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="select" value={status ?? ''} onChange={(e) => update({ status: e.target.value || undefined })}>
            <option value="">Todos los estados</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
          {perms.isSuper && (
            <select className="select" value={groupId ?? ''} onChange={(e) => update({ groupId: e.target.value || undefined })}>
              <option value="">Todos los grupos</option>
              {groups.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          )}
          <div className="spacer" />
          {jobs.data && <span className="muted">{fmtNumber(jobs.data.total)} trabajos</span>}
        </div>

        <ErrorBox error={jobs.error} />
        {jobs.isLoading ? (
          <Loading />
        ) : !jobs.data?.items.length ? (
          <Empty icon={FolderKanban} title="No hay trabajos">
            {status || q ? 'Prueba a cambiar los filtros.' : 'Los documentos enviados aparecerán aquí.'}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Estado</th>
                  <th>Páginas</th>
                  <th>Enviado por</th>
                  <th>Fecha</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {jobs.data.items.map((job) => (
                  <tr key={job.id} className="clickable" onClick={() => update({ job: job.id })}>
                    <td>
                      <div className="cell-main" title={job.fileName}>
                        {job.fileName}
                      </div>
                      <div className="cell-sub">
                        {job.kind.toUpperCase()} · {fmtBytes(job.sizeBytes)}
                        {job.mode === 'force' && ' · OCR forzado'}
                      </div>
                    </td>
                    <td>
                      <StatusBadge status={job.status} />
                      {job.error && (
                        <div className="cell-sub" style={{ color: 'var(--danger)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }} title={job.error.message}>
                          {job.error.message}
                        </div>
                      )}
                    </td>
                    <td>
                      <JobProgress job={job} />
                    </td>
                    <td>
                      <Creator job={job} />
                      {perms.isSuper && <div className="cell-sub">{job.groupName}</div>}
                    </td>
                    <td title={fmtDate(job.createdAt)}>
                      {fmtShortDate(job.createdAt)}
                      {job.durationMs !== null && <div className="cell-sub">{fmtDuration(job.durationMs)}</div>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row-actions">
                        {job.status === 'done' && job.filesAvailable && (
                          <a className="btn btn-icon" href={`${API}/jobs/${job.id}/result`} title="Descargar PDF" aria-label="Descargar PDF">
                            <Download size={16} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {jobs.data && <Pagination offset={offset} limit={LIMIT} total={jobs.data.total} onChange={(o) => update({ offset: String(o) })} />}
      </div>

      {selected && <JobDrawer id={selected} onClose={() => update({ job: undefined })} />}
    </>
  );
}

function JobDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const perms = usePermissions();
  const [confirm, setConfirm] = useState<'cancel' | 'delete' | null>(null);

  const job = useQuery({ queryKey: ['job', id], queryFn: () => get<Job>(`/jobs/${id}`) });
  const j = job.data;
  const report = useQuery({
    queryKey: ['job-report', id],
    queryFn: () => get<{ pages: PageReport[] }>(`/jobs/${id}/text?format=json`),
    enabled: j?.status === 'done' && j.filesAvailable,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['jobs'] });
    void qc.invalidateQueries({ queryKey: ['job', id] });
  };

  return (
    <Drawer
      title={j?.fileName ?? 'Trabajo'}
      onClose={onClose}
      headerExtra={j && <div style={{ marginTop: 6 }}><StatusBadge status={j.status} /></div>}
    >
      <ErrorBox error={job.error} />
      {!j ? (
        <Loading />
      ) : (
        <>
          {j.error && (
            <div className="alert alert-error">
              <div>
                <strong>{j.error.code}</strong>
                <div>{j.error.message}</div>
              </div>
            </div>
          )}
          {!j.filesAvailable && (
            <div className="alert alert-warn">Los archivos de este trabajo ya se han eliminado (retención o borrado manual).</div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
            {j.status === 'done' && j.filesAvailable && (
              <>
                <a className="btn btn-primary" href={`${API}/jobs/${j.id}/result`}>
                  <Download size={16} /> Descargar PDF
                </a>
                <a className="btn" href={`${API}/jobs/${j.id}/text`} target="_blank" rel="noreferrer">
                  <FileText size={16} /> Texto
                </a>
              </>
            )}
            {perms.canModifyJob(j) && (j.status === 'queued' || j.status === 'processing') && (
              <button className="btn btn-danger" onClick={() => setConfirm('cancel')}>
                <Ban size={16} /> Cancelar
              </button>
            )}
            {perms.canModifyJob(j) && j.filesAvailable && j.status !== 'queued' && j.status !== 'processing' && (
              <button className="btn btn-danger" onClick={() => setConfirm('delete')}>
                <Trash2 size={16} /> Borrar archivos
              </button>
            )}
          </div>

          {j.status === 'processing' && j.pages && (
            <div style={{ marginBottom: 18 }}>
              <div className="muted" style={{ marginBottom: 6 }}>
                Página {j.pages.done} de {j.pages.total}
              </div>
              <Progress value={j.pages.total ? j.pages.done / j.pages.total : 0} />
            </div>
          )}

          <dl className="dl">
            <dt>Formato</dt>
            <dd>
              {j.kind.toUpperCase()} · {fmtBytes(j.sizeBytes)}
            </dd>
            <dt>Resultado</dt>
            <dd>{fmtBytes(j.outputBytes)}</dd>
            <dt>Páginas</dt>
            <dd>
              {j.pages
                ? `${j.pages.total} en total · ${j.pages.ocr} con OCR · ${j.pages.skipped} ya tenían texto${j.pages.failed ? ` · ${j.pages.failed} fallidas` : ''}`
                : '—'}
            </dd>
            <dt>Modo</dt>
            <dd>{j.mode === 'force' ? 'OCR de todas las páginas' : 'Omitir páginas con texto'}</dd>
            <dt>Grupo</dt>
            <dd>{j.groupName}</dd>
            <dt>Enviado por</dt>
            <dd>
              {j.createdBy ? `${j.createdBy.type === 'apiKey' ? 'API key' : 'Usuario'}: ${j.createdBy.name ?? `#${j.createdBy.id}`}` : '—'}
            </dd>
            <dt>Creado</dt>
            <dd>{fmtDate(j.createdAt)}</dd>
            <dt>Iniciado</dt>
            <dd>{fmtDate(j.startedAt)}</dd>
            <dt>Terminado</dt>
            <dd>{fmtDate(j.finishedAt)}</dd>
            <dt>Duración</dt>
            <dd>{fmtDuration(j.durationMs)}</dd>
            <dt>Motor</dt>
            <dd>
              {j.provider ?? '—'}
              {j.attempts > 1 && ` · ${j.attempts} intentos`}
            </dd>
            <dt>ID</dt>
            <dd className="mono faint">{j.id}</dd>
          </dl>

          {report.data && (
            <>
              <div className="section-title">Texto por página</div>
              {report.data.pages.map((p) => (
                <div key={p.page}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>Página {p.page}</strong>
                    <span className="cell-sub">
                      {p.status === 'ocr'
                        ? `${p.words} palabras · confianza ${p.confidence ?? '—'} %`
                        : p.status === 'skipped-text'
                          ? 'Ya tenía texto'
                          : `Error: ${p.error}`}
                    </span>
                  </div>
                  <div className="page-text">{p.text || <span className="faint">(sin texto)</span>}</div>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {confirm === 'cancel' && j && (
        <Confirm
          title="Cancelar trabajo"
          message={<>¿Cancelar el procesamiento de <strong>{j.fileName}</strong>?</>}
          confirmLabel="Cancelar trabajo"
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await post(`/jobs/${j.id}/cancel`);
            toast('Trabajo cancelado');
            refresh();
          }}
        />
      )}
      {confirm === 'delete' && j && (
        <Confirm
          title="Borrar archivos"
          message={
            <>
              Se borrarán el documento original y el PDF resultante de <strong>{j.fileName}</strong>. El registro se
              conserva en el historial. Esta acción no se puede deshacer.
            </>
          }
          confirmLabel="Borrar archivos"
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            await del(`/jobs/${j.id}`);
            toast('Archivos borrados');
            refresh();
          }}
        />
      )}
    </Drawer>
  );
}
